import React, { useEffect, useState } from "react";
import { Form } from "antd";
import type { FormInstance } from "antd/es/form";
import {
  LOD2,
  BasedOn,
  BloomType,
  sanSub,
  sadSub,
  satSub,
  assrSub,
  matrixSub,
  paragraphSub,
  qstemSub,
  matchListSub,
  languageArrayOption,
  ConceptType,
} from "utilFunctions/Constants/Constants";
import MultiOnlyNodePicker from "Components/CommonComponents/MultiOnlyNodePicker";
import useMasterContext from "Context/MasterContext";
import DynamicFilterForm from "Pages/MySchedule/MyScheduledBeta/quiz/QuizBuilder/QuizManualPickQuestions/PickQuestionsFilter/DynamicFilterForm";
interface FilterState {
  isPaneOpen: boolean;
  selectedTag: {
    course?: { id?: number };
    newnode?: number[];
    node?: number[];
  };
}

interface PickQuestionsFilterProps {
  filterState: FilterState;
  setfilterState: React.Dispatch<React.SetStateAction<FilterState>>;
  form: FormInstance;
  onFinish: (values: any) => void;
  initialValues?: Record<string, any>;
  handleReset: () => void;
  syllabusTags: Array<{ value: number }>;
  refresh: number;
}

type QuestionTypeKey = keyof typeof questionTypeObj;

/* -------------------------------------------------------------------------- */
/*                              CONSTANT MAPPING                              */
/* -------------------------------------------------------------------------- */

export const questionTypeObj: Record<string, any[]> = {
  SAN: sanSub,
  SAD: sadSub,
  SAT: satSub,
  ASSR: assrSub,
  MATRIX: matrixSub,
  Scenario: paragraphSub,
  QSTEM: qstemSub,
  MATCHLIST: matchListSub,
};

/* -------------------------------------------------------------------------- */
/*                               COMPONENT                                    */
/* -------------------------------------------------------------------------- */

const PickQuestionsFilter: React.FC<PickQuestionsFilterProps> = ({
  filterState,
  setfilterState,
  form,
  onFinish,
  initialValues,
  handleReset,
  syllabusTags,
  refresh,
}) => {
  const { masters } = useMasterContext();
  const { boards, userAcadYear, exams } = masters || {};
  // const [subState, setSubState] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([
    { newcourse: { courseName: "" }, newnode: [] },
  ]);

  /* -------------------------------------------------------------------------- */
  /*                                FORM CONFIG                                 */
  /* -------------------------------------------------------------------------- */

  const [filterConfig, setFilterConfig] = useState<any>({
    basicFields: [
      {
        name: "lod2",
        label: "LOD2",
        componentType: "select",
        options: LOD2,
        span: 12,
        placeholder: "select",
      },
      {
        name: "freshQuestion",
        label: "Fresh Questions Only",
        componentType: "switch",
        span: 12,
        placeholder: "select",
      },

      {
        name: "boardId",
        label: "Board",
        componentType: "select",
        options: boards,
        span: 12,
        placeholder: "Select Board",
      },
      {
        name: "academicYearId",
        label: "Academic Year",
        componentType: "select",
        options: userAcadYear,
        span: 12,
        placeholder: "Select Academic Year",
      },
      {
        name: "examId",
        label: "Exam",
        componentType: "select",
        options: exams,
        span: 12,
        placeholder: "Select Exam",
      },
      {
        name: "questionBasedOn",
        label: "Based On",
        componentType: "select",
        options: BasedOn,
        span: 12,
        placeholder: "Select Based On",
      },
      {
        name: "bloomLevel",
        label: "Bloom Level",
        componentType: "select",
        options: BloomType,
        span: 12,
        placeholder: "Select Bloom Level",
      },
      {
        name: "tags",
        label: "Tags",
        componentType: "input",
        span: 12,
        placeholder: "select",
      },
      {
        name: "questionText",
        label: "Text",
        componentType: "custom",
        customType: "languageInput",
        span: 12,
        placeholder: "select",
      },
      {
        name: "questionConcept",
        label: "Concept",
        componentType: "select",
        options: ConceptType,
        span: 12,
        placeholder: "Select Concept",
      },
      {
        name: "marks",
        label: "Marks",
        componentType: "input-number",
        min: 1,
        tooltip: "Marks assigned to the question",
        span: 12,
        placeholder: "select",
      },
      {
        name: "questionLanguages",
        label: "Language",
        componentType: "multi-select",
        options: languageArrayOption.filter((ele) => ele.value != null),
        span: 12,
        placeholder: "Select Language",
      },
      {
        name: "doRandomization",
        label: "Randomized Questions",
        componentType: "switch",
        options: LOD2,
        span: 12,
        placeholder: "select",
      },
    ],
  });

  /* -------------------------------------------------------------------------- */
  /*                               EFFECTS                                      */
  /* -------------------------------------------------------------------------- */

  const questionTypeChanged = Form.useWatch("questionType", form);

  useEffect(() => {
    if (!questionTypeChanged) return;

    const options =
      questionTypeObj[questionTypeChanged as QuestionTypeKey] || [];
    // setSubState(options);

    setFilterConfig((prev: any) => ({
      ...prev,
      basicFields: prev.basicFields.map((field: any) =>
        field.name === "subQuestionType"
          ? { ...field, disabled: !options.length, options }
          : field
      ),
    }));
  }, [questionTypeChanged]);

  useEffect(() => {
    if (!syllabusTags?.length) return;

    setfilterState((prev) => ({
      ...prev,
      selectedTag: {
        ...prev.selectedTag,
        course: { id: syllabusTags[0]?.value },
        newnode: syllabusTags.map((s) => s.value),
        node: syllabusTags.map((s) => s.value),
      },
    }));
  }, [syllabusTags]);

  /* -------------------------------------------------------------------------- */
  /*                                DRAWER                                      */
  /* -------------------------------------------------------------------------- */

  const drawerConfig = {
    title: "Pick Topic",
    visible: filterState.isPaneOpen,
    onClose: () => setfilterState((prev) => ({ ...prev, isPaneOpen: false })),
    content: (
      <MultiOnlyNodePicker
        isChecked
        isMutliNodePicker
        moduleType="questionPickInTest"
        state={filterState}
        selectedNodes={filterState?.selectedTag?.newnode}
        tags={tags}
        settags={setTags}
        refresh={refresh}
        setState={(data: FilterState) => {
          setfilterState(data);
          if (data?.selectedTag?.newnode?.length) {
            form.setFieldValue(
              "questionTag",
              data.selectedTag.newnode.length === 1
                ? "Single Node"
                : "Multiple Nodes"
            );
          }
        }}
      />
    ),
  };

  /* -------------------------------------------------------------------------- */
  /*                                  RENDER                                    */
  /* -------------------------------------------------------------------------- */

  return (
    <DynamicFilterForm
      formConfig={filterConfig}
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
      onReset={() => {
        form.resetFields();
        handleReset();
      }}
      drawerConfig={drawerConfig}
    />
  );
};

export default PickQuestionsFilter;
